import React, { useEffect, useState } from "react";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem, Stack,
  Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { History, Pencil, Plus, Trash2, X } from "lucide-react";
import { useHd } from "../HelpdeskContext";
import { hd } from "../api";
import ConditionBuilder from "./ConditionBuilder";
import { Section, useCrud } from "./common";
import { T, TYPE, formatDateTime, labelSx, primaryButtonSx } from "../../user/complaints/shared";

const th = { "& th": { ...labelSx, bgcolor: T.surfaceHeader } };

const TRIGGERS = [
  ["ticket.created", "Ticket created"], ["email.received", "Email received"], ["chat.started", "Chat started"], ["message.received", "Message received"],
  ["ticket.updated", "Ticket updated"], ["customer.replied", "Customer replied"], ["agent.replied", "Agent replied"], ["status.changed", "Status changed"],
  ["priority.changed", "Priority changed"], ["sla.warning", "SLA warning"], ["sla.breached", "SLA breached"], ["time.based", "Time based (checked every 5 min)"],
];

const ACTIONS = [
  ["assign_team", "Assign team"], ["assign_agent", "Assign agent"], ["set_status", "Change status"], ["set_priority", "Change priority"],
  ["set_category", "Set category"], ["add_tags", "Add tags"], ["remove_tags", "Remove tags"], ["set_field", "Set custom field"],
  ["send_template", "Send email template"], ["send_email", "Send email"], ["send_whatsapp", "Send WhatsApp"], ["send_notification", "Notify agents"],
  ["create_task", "Create task"], ["escalate", "Escalate"], ["webhook", "Call webhook"], ["ai_triage", "Run AI triage"], ["add_note", "Add internal note"],
];

function ActionParams({ a, onChange }) {
  const ctx = useHd();
  const p = a.params || {};
  const set = (k) => (e) => onChange({ ...a, params: { ...p, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e } });
  const sel = (k, label, opts, extra = {}) => (
    <TextField select size="small" label={label} value={p[k] ?? ""} onChange={set(k)} sx={{ minWidth: 180 }} {...extra}>
      {opts.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
    </TextField>
  );
  const text = (k, label, extra = {}) => <TextField size="small" label={label} value={p[k] ?? ""} onChange={set(k)} sx={{ minWidth: 200 }} {...extra} />;
  switch (a.type) {
    case "assign_team": return <>{sel("teamId", "Team", ctx.teams.map((t) => [t._id, t.name]))}<FormControlLabel control={<Checkbox size="small" checked={p.autoAssign !== false} onChange={set("autoAssign")} />} label="auto-assign agent" /></>;
    case "assign_agent": return sel("memberId", "Agent", ctx.agents.map((x) => [x.memberId, x.name]));
    case "set_status": return sel("statusId", "Status", ctx.statuses.map((s) => [s._id, s.name]));
    case "set_priority": return sel("priorityId", "Priority", ctx.priorities.map((s) => [s._id, s.name]));
    case "set_category": return sel("categoryId", "Category", ctx.categories.filter((c) => c.level === "category").map((c) => [c._id, c.name]));
    case "add_tags": case "remove_tags": return <TextField size="small" label="Tags (comma separated)" value={(p.tags || []).join(", ")} onChange={(e) => onChange({ ...a, params: { tags: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })} />;
    case "set_field": return <>{sel("key", "Field", ctx.customFields.filter((f) => f.entity === "ticket").map((f) => [f.key, f.label]))}{text("value", "Value")}</>;
    case "send_template": return sel("event", "Template", [["ticket.created", "Acknowledgement"], ["ticket.resolved", "Resolved"], ["ticket.closed", "Closed"], ["csat.request", "CSAT"], ["agent.replied", "Agent reply"]]);
    case "send_email": return <Stack spacing={1} sx={{ flexGrow: 1 }}>{sel("to", "To", [["requester", "Customer"], ["assignee", "Assigned agent"], ["custom", "Other address"]])}{p.to === "custom" && text("address", "Email address")}{text("subject", "Subject (variables allowed)")}{text("body", "Body", { multiline: true, minRows: 3 })}</Stack>;
    case "send_whatsapp": return <Stack spacing={1} sx={{ flexGrow: 1 }}>{sel("to", "To", [["requester", "Customer phone"], ["custom", "Other number"]])}{p.to === "custom" && text("phone", "Phone")}{text("message", "Message", { multiline: true, minRows: 2 })}</Stack>;
    case "send_notification": return <>{sel("to", "Who", [["assignee", "Assigned agent (or team)"], ["team", "A team"], ["member", "A specific agent"]])}{p.to === "team" && sel("teamId", "Team", ctx.teams.map((t) => [t._id, t.name]))}{p.to === "member" && sel("memberId", "Agent", ctx.agents.map((x) => [x.memberId, x.name]))}{text("title", "Title")}</>;
    case "create_task": return <>{text("title", "Task title")}{text("dueInHours", "Due in (hours)", { type: "number" })}</>;
    case "escalate": return <>{sel("teamId", "Move to team (optional)", [["", "—"], ...ctx.teams.map((t) => [t._id, t.name])])}<FormControlLabel control={<Checkbox size="small" checked={Boolean(p.raisePriority)} onChange={set("raisePriority")} />} label="raise priority" /></>;
    case "webhook": return <>{text("url", "URL (https://…)")}{text("secret", "Signing secret (optional)")}</>;
    case "add_note": return text("content", "Note (variables allowed)", { multiline: true, minRows: 2 });
    default: return null;
  }
}

export default function AutomationSettings() {
  const ctx = useHd();
  const { rows, create, update, remove } = useCrud("/automations");
  const [edit, setEdit] = useState(null);
  const [runsFor, setRunsFor] = useState(null);
  const manage = ctx.can("automation.manage");
  if (!rows) return <CircularProgress size={22} />;
  const save = async () => {
    const body = { name: edit.name, description: edit.description, trigger: edit.trigger, conditions: edit.conditions, actions: edit.actions, order: Number(edit.order), stopProcessing: edit.stopProcessing, enabled: edit.enabled };
    const ok = edit._id ? await update(edit._id, body) : await create(body);
    if (ok) setEdit(null);
  };
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>WHEN a trigger fires, IF the conditions match, THEN run the actions. Rules run in order.</Typography>
        {manage && <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setEdit({ name: "", description: "", trigger: "ticket.created", conditions: { all: [], any: [] }, actions: [{ type: "assign_team", params: {} }], order: (rows.length + 1) * 10, stopProcessing: false, enabled: true })}>Add rule</Button>}
      </Stack>
      <Table size="small">
        <TableHead><TableRow sx={th}><TableCell>Order</TableCell><TableCell>Rule</TableCell><TableCell>When</TableCell><TableCell>Then</TableCell><TableCell>Runs</TableCell><TableCell>On</TableCell><TableCell /></TableRow></TableHead>
        <TableBody>
          {[...rows].sort((a, b) => a.order - b.order).map((r) => (
            <TableRow key={r._id}>
              <TableCell>{r.order}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
              <TableCell>{TRIGGERS.find((t) => t[0] === r.trigger)?.[1]}</TableCell>
              <TableCell>{r.actions.map((a, i) => <Chip key={i} size="small" sx={{ mr: 0.5 }} label={ACTIONS.find((x) => x[0] === a.type)?.[1] || a.type} />)}</TableCell>
              <TableCell>{r.runCount}{r.lastRunAt ? <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatDateTime(r.lastRunAt)}</Typography> : null}</TableCell>
              <TableCell><Switch size="small" checked={r.enabled} disabled={!manage} onChange={(e) => update(r._id, { enabled: e.target.checked })} /></TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                <IconButton size="small" onClick={() => setRunsFor(r)}><History size={15} /></IconButton>
                {manage && <><IconButton size="small" onClick={() => setEdit({ ...r })}><Pencil size={15} /></IconButton><IconButton size="small" onClick={() => remove(r._id)}><Trash2 size={15} color="#B42318" /></IconButton></>}
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && <TableRow><TableCell colSpan={7} sx={{ color: T.inkMuted }}>No automation rules yet</TableCell></TableRow>}
        </TableBody>
      </Table>
      {edit && (
        <Dialog open onClose={() => setEdit(null)} maxWidth="md" fullWidth>
          <DialogTitle>{edit._id ? "Edit rule" : "New rule"}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={8}><TextField fullWidth label="Name" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField fullWidth type="number" label="Order" value={edit.order} onChange={(e) => setEdit((p) => ({ ...p, order: e.target.value }))} /></Grid>
            </Grid>
            <Section title="WHEN">
              <TextField select fullWidth size="small" value={edit.trigger} onChange={(e) => setEdit((p) => ({ ...p, trigger: e.target.value }))}>{TRIGGERS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}</TextField>
            </Section>
            <Section title="IF"><ConditionBuilder value={edit.conditions} onChange={(conditions) => setEdit((p) => ({ ...p, conditions }))} /></Section>
            <Section title="THEN">
              {edit.actions.map((a, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.25, p: 1.25, border: `1px solid ${T.border}`, borderRadius: "10px" }} flexWrap="wrap" useFlexGap>
                  <TextField select size="small" value={a.type} onChange={(e) => setEdit((p) => ({ ...p, actions: p.actions.map((x, j) => (j === i ? { type: e.target.value, params: {} } : x)) }))} sx={{ minWidth: 190 }}>
                    {ACTIONS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                  </TextField>
                  <ActionParams a={a} onChange={(na) => setEdit((p) => ({ ...p, actions: p.actions.map((x, j) => (j === i ? na : x)) }))} />
                  <IconButton size="small" onClick={() => setEdit((p) => ({ ...p, actions: p.actions.filter((_, j) => j !== i) }))}><X size={15} /></IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<Plus size={14} />} onClick={() => setEdit((p) => ({ ...p, actions: [...p.actions, { type: "add_tags", params: {} }] }))}>Add action</Button>
            </Section>
            <FormControlLabel control={<Checkbox checked={edit.stopProcessing} onChange={(e) => setEdit((p) => ({ ...p, stopProcessing: e.target.checked }))} />} label="Stop running later rules when this one matches" />
            <FormControlLabel control={<Checkbox checked={edit.enabled} onChange={(e) => setEdit((p) => ({ ...p, enabled: e.target.checked }))} />} label="Enabled" />
          </DialogContent>
          <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button></DialogActions>
        </Dialog>
      )}
      {runsFor && <RunsDialog rule={runsFor} onClose={() => setRunsFor(null)} />}
    </Box>
  );
}

function RunsDialog({ rule, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    hd.automationRuns({ ruleId: rule._id }).then(setRows).catch(() => setRows([]));
  }, [rule._id]);
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Runs — {rule.name}</DialogTitle>
      <DialogContent dividers>
        {!rows ? <CircularProgress size={20} /> : (
          <Table size="small">
            <TableHead><TableRow sx={th}><TableCell>When</TableCell><TableCell>Matched</TableCell><TableCell>Actions</TableCell><TableCell>ms</TableCell></TableRow></TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r._id}>
                  <TableCell>{formatDateTime(r.createdAt)}</TableCell>
                  <TableCell>{r.matched ? "Yes" : "No"}</TableCell>
                  <TableCell>{r.actions.map((a, i) => <Chip key={i} size="small" color={a.ok ? "success" : "error"} variant="outlined" sx={{ mr: 0.5 }} label={a.ok ? a.type : `${a.type}: ${a.error}`} />)}</TableCell>
                  <TableCell>{r.durationMs}</TableCell>
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={4} sx={{ color: T.inkMuted }}>No runs yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
